import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, TextField, Grid, Paper, Alert } from '@mui/material';
import { useWizard } from '@/components/wizard/useWizard';
import { WizardLayout } from '@/components/wizard/WizardLayout';
import { WizardStep } from '@/components/wizard/WizardStep';
import { OutcomeSummary } from '@/components/wizard/OutcomeSummary';
import { StepFeedback } from '@/components/wizard/StepFeedback';
import { useDataProvider } from 'react-admin';
import { buildTransitionChain } from '@/components/workflow/machineHelpers';
import { useRuleEvaluation } from '@/components/workflow/useRuleEvaluation';

const STEPS = [
  { id: 'references', title: 'Tutor & Type' },
  { id: 'details', title: 'Details' },
  { id: 'review', title: 'Review & Submit' },
];

interface FormValues {
  tutorId: string;
  qualificationTypeId: string;
  name: string;
  issuer: string;
  issuedDate: string;
  expiryDate: string;
}

const INITIAL_VALUES: FormValues = {
  tutorId: '',
  qualificationTypeId: '',
  name: '',
  issuer: '',
  issuedDate: '',
  expiryDate: '',
};

const stepDescriptions: Record<string, string> = {
  references: "Select the tutor and qualification type for this qualification record.",
  details: "Enter the qualification name, issuing institution, and dates. Link to a training provider if registered.",
  review: "Review the qualification before creating.",
};

export const QualificationWizard = () => {
  const navigate = useNavigate();
  const dataProvider = useDataProvider();

  const handleWizardComplete = useCallback(async (formValues: Record<string, unknown>) => {
    const v = formValues as unknown as FormValues;

    const entityResult = await dataProvider.create('qualifications', {
      data: {
        tutor: { type: 'tutor', id: v.tutorId },
        qualificationType: { type: 'qualificationtype', id: v.qualificationTypeId },
        name: v.name,
        issuer: v.issuer,
        issuedDate: v.issuedDate,
        expiryDate: v.expiryDate || undefined,
        verified: false,
      },
    });

    const entityId = entityResult.data.id;
    const { finalState, transitions } = buildTransitionChain('qualification', 'draft', ['VERIFY']);

    await dataProvider.create('workflow-instances', {
      data: {
        workflowType: 'qualification',
        entityId,
        entityName: v.name,
        currentState: { value: finalState, context: v },
        status: 'active',
        transitions,
      },
    });

    return { id: entityId };
  }, [dataProvider]);

  const {
    currentStep, currentStepIndex, totalSteps, isFirstStep, isLastStep,
    isSaving, isCompleted, progress, values, errors, stepFeedback,
    nextStep, prevStep, updateValues, complete, reset,
  } = useWizard({
    steps: STEPS,
    initialValues: INITIAL_VALUES as unknown as Record<string, unknown>,
    dataProvider,
    onComplete: handleWizardComplete,
  });

  const handleNext = () => {
    if (isLastStep) {
      complete();
    } else {
      nextStep();
    }
  };

  const updateField = (field: keyof FormValues, value: unknown) => {
    updateValues({ [field]: value });
  };

  const vals = values as unknown as FormValues;
  const { evaluate } = useRuleEvaluation({ ruleId: 'qualification:create', context: vals as unknown as Record<string, unknown> });

  const renderFormFields = () => {
    switch (currentStep.id) {
      case 'references':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Alert severity="info" sx={{ mb: 2 }}>Select the tutor and qualification type.</Alert>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Tutor ID" value={vals.tutorId} onChange={e => updateField('tutorId', e.target.value)} fullWidth required />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Qualification Type ID" value={vals.qualificationTypeId} onChange={e => updateField('qualificationTypeId', e.target.value)} fullWidth required />
            </Grid>
          </Grid>
        );
      case 'details':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Qualification Name" value={vals.name} onChange={e => updateField('name', e.target.value)} fullWidth required />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Issuer" value={vals.issuer} onChange={e => updateField('issuer', e.target.value)} fullWidth required />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Issue Date" type="date" value={vals.issuedDate} onChange={e => updateField('issuedDate', e.target.value)} fullWidth required InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Expiry Date" type="date" value={vals.expiryDate} onChange={e => updateField('expiryDate', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
          </Grid>
        );
      case 'review':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Alert severity="info" sx={{ mb: 2 }}>Review qualification details before creating.</Alert>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2">References</Typography>
                <Typography variant="body2"><strong>Tutor ID:</strong> {vals.tutorId}</Typography>
                <Typography variant="body2"><strong>Type ID:</strong> {vals.qualificationTypeId}</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2">Details</Typography>
                <Typography variant="body2"><strong>Name:</strong> {vals.name}</Typography>
                <Typography variant="body2"><strong>Issuer:</strong> {vals.issuer}</Typography>
                <Typography variant="body2"><strong>Issued:</strong> {vals.issuedDate}</Typography>
                <Typography variant="body2"><strong>Expires:</strong> {vals.expiryDate || 'Never'}</Typography>
              </Paper>
            </Grid>
          </Grid>
        );
      default:
        return null;
    }
  };

  return (
    <WizardLayout
      steps={STEPS}
      currentStepIndex={currentStepIndex}
      progress={progress}
      isSaving={isSaving}
      isCompleted={isCompleted}
      isFirstStep={isFirstStep}
      isLastStep={isLastStep}
      onBack={prevStep}
      onSaveDraft={() => {}}
      onNext={handleNext}
      onComplete={complete}
      onReset={reset}
    >
      {isCompleted ? (
        <OutcomeSummary
          title="Qualification Created"
          processId={(values as { id: string }).id}
          fields={[
            { label: 'Name', value: vals.name },
            { label: 'Issuer', value: vals.issuer },
            { label: 'Issued', value: vals.issuedDate },
          ]}
          footerActions={[
            { label: 'View Qualifications', to: '/qualifications' },
            { label: 'Create Another', onClick: reset },
          ]}
        />
      ) : (
        <WizardStep
          stepNumber={currentStepIndex + 1}
          totalSteps={totalSteps}
          title={currentStep.title}
          description={stepDescriptions[currentStep.id]}
        >
          {stepFeedback && <StepFeedback type={stepFeedback.type} messages={stepFeedback.messages} />}
          {renderFormFields()}
        </WizardStep>
      )}
    </WizardLayout>
  );
};
